import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { LunaEntity, PropertyMap, EntityMap, RelatedEntityMap } from "../models/entity.model";
import { genId } from "../../core/services/genId";



@Injectable()
export class EntityBuilderService {
    public static baseUrl = "/api/entity";
    constructor(public http: HttpClient) {

    }

    get(id: string): Observable<LunaEntity> {
        return this.http.get<LunaEntity>(`${EntityBuilderService.baseUrl}/${id}`);
    }

    post(body: LunaEntity): Observable<LunaEntity> {
        if (!body.id) body.id = genId();
        return this.http.post<LunaEntity>(`${EntityBuilderService.baseUrl}/`, body);
    }

    getAll(): Observable<Array<LunaEntity>> {
        return this.http.get<Array<LunaEntity>>(`${EntityBuilderService.baseUrl}/`);
    }

    getActive(): Observable<LunaEntity[]> {
        return this.http.get<LunaEntity[]>(`${EntityBuilderService.baseUrl}/getActive`);
    }

    //moved it out of entity.model.ts, was at wrong location

    isRelationalMap(map: any): map is RelatedEntityMap {
        if (!map) return false;
        if (map.relationalProperty) return true;
        return false;
    }

    generateEntityMap(entity: LunaEntity): EntityMap {
        const id = genId();
        const map = { id: id, mapName: entity.entityName, propertyMaps: [], relatedEntityMaps: [], entity: entity, relateCasingPlan: false, casingPlanContext: "" } as EntityMap;
        for (let i = 0; i < Object.keys(entity.schemaObject.properties).length; i++) {
            map.propertyMaps.push(this.generatePropertyMap(entity.schemaObject.properties, i));
        }
        return map;
    }

    generatePropertyMap(propertyData: any, index: number): PropertyMap {
        const map =
            {
                propertyName: "",
                propertyFormat: "",
                propertyType: "",
                propertyAllowEdit: false,
                propertyDisplayName: "",
                allowMultipleMaps: false,
                contextPath: "",
                isPrimitive: true,
                mapWholeNode: false,
                arrayFunction: "",
                addObjectFunction: "",
                resultCanBeArray: false,
            } as PropertyMap;

        map.propertyName = Object.keys(propertyData)[index];
        let propertyType: string | PropertyMap[] | Array<string | PropertyMap[]> = undefined;
        const value = Object.values(propertyData)[index];

        for (let v = 0; v < Object.keys(value).length; v++) {
            propertyType = this.getProperty(value, v, Object.keys(value)[v]);
            if (propertyType) break;
        }

        if ((Object.keys(value)).some(key => key == "format")) {
            map.propertyFormat = value["format"];
        }

        map.propertyType = (propertyType) ? propertyType : "";
        const hasItems = Object.keys(value).filter(k => k === "items");
        const hasProperties = Object.keys(value).filter(k => k === "properties");
        map.allowMultipleMaps = (hasItems && hasItems.length > 0);
        map.isPrimitive = (!hasItems || hasItems.length === 0) && (!hasProperties || hasProperties.length === 0);
        return map;
    }

    getProperty(data: any, index: number, value: string): string | PropertyMap[] | Array<string | PropertyMap[]> {
        let property: any = undefined;
        if (value === "type") {
            const type = Object.values(data)[index];
            if (type !== "array" && type !== "object") {
                property = type.toString();
            }
        } else if (Object.keys(data)[index] === "properties") {
            const keys = Object.values(data)[index];
            property = new Array<PropertyMap>();
            for (let i = 0; i < Object.keys(keys).length; i++) {
                property.push(this.generatePropertyMap(keys, i));
            }
        } else if (Object.keys(data)[index] === "items") {
            const keys = Object.values(data)[index];
            property = new Array<string | PropertyMap[] | Array<string | PropertyMap>>();
            for (let i = 0; i < Object.keys(keys).length; i++) {
                const prop = this.getProperty(Object.values(data)[index], i, Object.keys(Object.values(data)[index])[i]);

                if (prop === Object(prop)) {
                    const propMap = {
                        propertyName: "object",
                        propertyType: prop,
                        propertyFormat: "",
                        contextPath: "",
                        allowMultipleMaps: false,
                        isPrimitive: false,
                        mapWholeNode: false
                    } as PropertyMap;
                    property.push(propMap);
                } else if (prop !== undefined && prop !== null) {
                    property.push({
                        propertyName: prop,
                        propertyType: prop,
                        propertyFormat: "",                      
                        contextPath: "",
                        allowMultipleMaps: false,
                        isPrimitive: true,
                        mapWholeNode: false
                    } as PropertyMap);
                }
            }
        }
        return property;
    }

    updateEntityMap(oldEntity: EntityMap | RelatedEntityMap, newEntity: LunaEntity): EntityMap {
        const newMap = this.generateEntityMap(newEntity);

        let map: EntityMap | RelatedEntityMap = { id: "", mapName: "", entity: newEntity, propertyMaps: [], idSelectorRule: null, relatedEntityMaps: [] } as EntityMap;
        map.id = oldEntity.id; //preserved or this will cause problems for the save function
        map.mapName = newMap.mapName; //shouldn't make a difference; both entitymaps should have the same name when this is called
        map.idSelectorRule = oldEntity.idSelectorRule; //err on the side of keeping what's there
        if (oldEntity.relatedEntityMaps) {
            for (let p = 0; p < oldEntity.relatedEntityMaps.length; p++) {
                map.relatedEntityMaps.push(oldEntity.relatedEntityMaps[p]);
            } //copy all related entity maps verbatim
        }
        for (let m = 0; m < newMap.propertyMaps.length; m++) {
            let newProperty = newMap.propertyMaps[m];
            let existing = oldEntity.propertyMaps.find(pm => pm.propertyName === newProperty.propertyName);
            if (existing) {
                newProperty = this.updatePropertyMaps(existing, newProperty);
            }
            map.propertyMaps.push(newProperty);
        }
        if (this.isRelationalMap(oldEntity)) {
            const relational = map as RelatedEntityMap;
            relational.parentMapId = oldEntity.parentMapId;
            relational.relationalProperty = oldEntity.relationalProperty;
            map = relational;
        }
        return map;
    }

    updatePropertyMaps(currentPropertyMap: PropertyMap, newPropertyMap: PropertyMap): PropertyMap {
        //If propertyMap exists update the new map with the old values
        if (currentPropertyMap && currentPropertyMap.propertyName == newPropertyMap.propertyName) {
            if (newPropertyMap.propertyType === currentPropertyMap.propertyType
                && (newPropertyMap.isPrimitive && currentPropertyMap.isPrimitive)) {
                newPropertyMap.contextPath = currentPropertyMap.contextPath;
            }

            if (newPropertyMap.mapWholeNode && currentPropertyMap.mapWholeNode) {
                newPropertyMap.mapWholeNode = currentPropertyMap.mapWholeNode;
            }

            //If it's not a primitive type, run through all it's children
            if (!newPropertyMap.isPrimitive && !currentPropertyMap.isPrimitive) {
                for (let x = 0; x < newPropertyMap.propertyType.length; x++)
                    (newPropertyMap.propertyType as PropertyMap[])[x] = this.updatePropertyMaps((currentPropertyMap.propertyType as PropertyMap[])[x], (newPropertyMap.propertyType as PropertyMap[])[x]);
            }
        }

        return newPropertyMap;
    }
}


 


